import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell, BellOff, CalendarDays, Flame, Ticket } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { api, type CalendarPromo } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { PromoCountdown } from "@/components/promo/countdown";
import { promoDiscountLabel } from "@/components/promo/promo-price";
import { Skeleton } from "@osmix/ui/skeleton";
import { useFormatPrice } from "@/lib/use-format-price";
import { cn } from "@/lib/utils";
import { useProfile } from "@/store/profile";

// «Календарь акций» (П3, fd-promo-13 и fd-promo-33): публичные акции магазинов
// и площадки на ближайшие 30 дней. Акции сгруппированы по категориям товаров и
// отсортированы по окончанию — что кончится раньше, то выше. Фильтр — по
// категории и «Горящие». На ещё не начавшуюся акцию можно подписаться: в
// момент старта придёт уведомление.

const KIND_LABEL: Record<CalendarPromo["kind"], string> = {
  regular: "Акция магазина",
  flash: "Горящая",
  collaboration: "Коллаборация",
  platform: "Акция OSMIX",
  code: "Промокод"
};
const dayTime = (iso: string) => new Date(iso).toLocaleString("ru-RU", {
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit"
});
const day = (iso: string) => new Date(iso).toLocaleDateString("ru-RU", {
  day: "numeric",
  month: "long"
});
const chip = (active: boolean) => cn("inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3.5 text-[14px] leading-5 transition-colors", active ? "border-[#1F1F1F] bg-[#1F1F1F] text-white" : "border-osmix-gray-200 bg-white text-[#1F1F1F] hover:border-[#1F1F1F]");
export function PromosPage() {
  const $ = _c(64);
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") === "flash" ? "flash" : undefined;
  let t0;
  if ($[0] !== params) {
    t0 = params.get("category") ?? undefined;
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const categoryId = t0;
  const t1 = kind ?? "";
  let t2;
  if ($[2] !== t1) {
    t2 = ["promos", "calendar", t1, ""];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== kind) {
    t3 = () => api.promos.calendar({
      kind
    });
    $[4] = kind;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = {
      queryKey: t2,
      queryFn: t3
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const base = useQuery(t4);
  const t5 = kind ?? "";
  const t6 = categoryId ?? "";
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = ["promos", "calendar", t5, t6];
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== categoryId || $[13] !== kind) {
    t8 = () => api.promos.calendar({
      kind,
      categoryId
    });
    $[12] = categoryId;
    $[13] = kind;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = Boolean(categoryId);
  let t10;
  if ($[15] !== t7 || $[16] !== t8 || $[17] !== t9) {
    t10 = {
      queryKey: t7,
      queryFn: t8,
      enabled: t9
    };
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const filtered = useQuery(t10);
  const calendar = categoryId ? filtered : base;
  let t11;
  if ($[19] !== params || $[20] !== setParams) {
    t11 = (key, value) => {
      const next = new URLSearchParams(params);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      setParams(next, {
        replace: true
      });
    };
    $[19] = params;
    $[20] = setParams;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const setFilter = t11;
  const data = calendar.data;
  let t12;
  if ($[22] !== data?.items) {
    t12 = new Map((data?.items ?? []).map(_temp));
    $[22] = data?.items;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const byId = t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <div className="flex flex-col gap-1.5"><h1 className="flex items-center gap-2 text-[22px] font-semibold leading-7 text-[#1F1F1F] sm:text-[28px] sm:leading-9"><CalendarDays className="size-6 text-osmix-tangelo" aria-hidden={true} />Календарь акций</h1><p className="text-[14px] leading-5 text-[#5C5C5C] sm:text-[16px] sm:leading-6">Скидки магазинов и OSMIX на ближайшие 30 дней. Подпишитесь на акцию - напомним, когда она начнётся.</p></div>;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = !kind && !categoryId;
  let t15;
  if ($[25] !== t14) {
    t15 = chip(t14);
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== setParams) {
    t16 = () => setParams(new URLSearchParams(), {
      replace: true
    });
    $[27] = setParams;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== t15 || $[30] !== t16) {
    t17 = <button type="button" className={t15} onClick={t16}>Все</button>;
    $[29] = t15;
    $[30] = t16;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = kind === "flash";
  const t19 = kind === "flash";
  let t20;
  if ($[32] !== t19) {
    t20 = chip(t19);
    $[32] = t19;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== kind || $[35] !== setFilter) {
    t21 = () => setFilter("kind", kind === "flash" ? undefined : "flash");
    $[34] = kind;
    $[35] = setFilter;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Flame className="size-4" aria-hidden={true} />;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t18 || $[39] !== t20 || $[40] !== t21) {
    t23 = <button type="button" aria-pressed={t18} className={t20} onClick={t21}>{t22}Горящие</button>;
    $[38] = t18;
    $[39] = t20;
    $[40] = t21;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== base.data?.categories) {
    t24 = base.data?.categories ?? [];
    $[42] = base.data?.categories;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== categoryId || $[45] !== setFilter || $[46] !== t24) {
    let t26;
    if ($[48] !== categoryId || $[49] !== setFilter) {
      t26 = cat => <button key={cat.id} type="button" aria-pressed={categoryId === cat.id} className={chip(categoryId === cat.id)} onClick={() => setFilter("category", categoryId === cat.id ? undefined : cat.id)}>{cat.name}</button>;
      $[48] = categoryId;
      $[49] = setFilter;
      $[50] = t26;
    } else {
      t26 = $[50];
    }
    t25 = t24.map(t26);
    $[44] = categoryId;
    $[45] = setFilter;
    $[46] = t24;
    $[47] = t25;
  } else {
    t25 = $[47];
  }
  let t26;
  if ($[51] !== t17 || $[52] !== t23 || $[53] !== t25) {
    t26 = <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">{t17}{t23}{t25}</div>;
    $[51] = t17;
    $[52] = t23;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  if ($[55] !== byId || $[56] !== calendar || $[57] !== categoryId || $[58] !== data || $[59] !== kind) {
    t27 = calendar.isPending ? <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{["a", "b", "c"].map(_temp2)}</div> : calendar.error ? <ErrorState onRetry={() => calendar.refetch()} /> : !data || data.items.length === 0 ? <EmptyState icon={CalendarDays} title={kind || categoryId ? "\u0422\u0430\u043A\u0438\u0445 \u0430\u043A\u0446\u0438\u0439 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" : "\u0410\u043A\u0446\u0438\u0439 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442"} hint={"\u0417\u0430\u0433\u043B\u044F\u043D\u0438\u0442\u0435 \u043F\u043E\u0437\u0436\u0435 - \u043C\u0430\u0433\u0430\u0437\u0438\u043D\u044B \u0434\u043E\u0431\u0430\u0432\u043B\u044F\u044E\u0442 \u0430\u043A\u0446\u0438\u0438 \u043A\u0430\u0436\u0434\u044B\u0439 \u0434\u0435\u043D\u044C."} /> : data.groups.map(group => <section key={group.categoryId ?? "all"} className="flex flex-col gap-3"><h2 className="text-[16px] leading-5 text-[#1F1F1F] sm:text-[20px] sm:font-semibold sm:leading-[23px]">{group.categoryName}</h2><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{group.promoIds.map(id => {
          const promo = byId.get(id);
          return promo ? <PromoCard key={`${group.categoryId}-${id}`} promo={promo} onExpire={() => void calendar.refetch()} /> : null;
        })}</div></section>);
    $[55] = byId;
    $[56] = calendar;
    $[57] = categoryId;
    $[58] = data;
    $[59] = kind;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== t26 || $[62] !== t27) {
    t28 = <div className="flex flex-col gap-5 pb-6 sm:gap-6" data-testid="promos-page">{t13}{t26}{t27}</div>;
    $[61] = t26;
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  return t28;
}
function _temp2(key_0) {
  return <Skeleton key={key_0} className="h-[260px] w-full rounded-[12px]" />;
}
function _temp(item) {
  return [item.id, item] as const;
}
function PromoCard(t0) {
  const $ = _c(68);
  const {
    promo,
    onExpire
  } = t0;
  const fmt = useFormatPrice();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const token = useProfile(_temp3);
  let t1;
  if ($[0] !== promo.id || $[1] !== promo.subscribed) {
    t1 = () => promo.subscribed ? api.promos.unsubscribe(promo.id) : api.promos.subscribe(promo.id);
    $[0] = promo.id;
    $[1] = promo.subscribed;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== queryClient) {
    t2 = result => {
      toast.success(result.subscribed ? "\u041D\u0430\u043F\u043E\u043C\u043D\u0438\u043C, \u043A\u043E\u0433\u0434\u0430 \u0430\u043A\u0446\u0438\u044F \u043D\u0430\u0447\u043D\u0451\u0442\u0441\u044F" : "\u041D\u0430\u043F\u043E\u043C\u0438\u043D\u0430\u043D\u0438\u0435 \u043E\u0442\u043A\u043B\u044E\u0447\u0435\u043D\u043E");
      queryClient.invalidateQueries({
        queryKey: ["promos", "calendar"]
      });
    };
    $[3] = queryClient;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: _temp4
    };
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const subscribe = useMutation(t3);
  let t4;
  if ($[8] !== navigate || $[9] !== subscribe || $[10] !== token) {
    t4 = () => {
      if (!token) {
        toast("\u0412\u043E\u0439\u0434\u0438\u0442\u0435, \u0447\u0442\u043E\u0431\u044B \u043F\u043E\u043B\u0443\u0447\u0438\u0442\u044C \u043D\u0430\u043F\u043E\u043C\u0438\u043D\u0430\u043D\u0438\u0435", {
          action: {
            label: "\u0412\u043E\u0439\u0442\u0438",
            onClick: () => navigate("/profile")
          }
        });
        return;
      }
      subscribe.mutate();
    };
    $[8] = navigate;
    $[9] = subscribe;
    $[10] = token;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const toggleSubscription = t4;
  const flashActive = promo.kind === "flash" && promo.state === "active" && promo.endsAt;
  const shop = promo.shops[0];
  const t5 = `promo-card-${promo.id}`;
  const t6 = promo.kind === "flash" ? "bg-osmix-tangelo/10 text-osmix-tangelo" : "bg-osmix-gray-100 text-[#5C5C5C]";
  let t7;
  if ($[12] !== t6) {
    t7 = cn("self-start rounded-full px-2 py-0.5 text-[12px] leading-4", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = KIND_LABEL[promo.kind];
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <span className={t7}>{t8}</span>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== promo.title) {
    t10 = <h3 className="text-[16px] font-semibold leading-5 text-[#1F1F1F]">{promo.title}</h3>;
    $[17] = promo.title;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== promo.shops) {
    t11 = promo.shops.length > 0 && <p className="text-[13px] leading-[18px] text-[#5C5C5C]">{promo.shops.map(_temp5)}</p>;
    $[19] = promo.shops;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t9) {
    t12 = <div className="flex min-w-0 flex-col gap-1">{t9}{t10}{t11}</div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== promo.discountType || $[26] !== promo.discountValue) {
    t13 = promoDiscountLabel(promo.discountType, promo.discountValue);
    $[25] = promo.discountType;
    $[26] = promo.discountValue;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <span className="shrink-0 rounded-full bg-osmix-tangelo px-2.5 py-1 text-[14px] font-semibold leading-4 text-white">{t13}</span>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <div className="flex items-start justify-between gap-3">{t12}{t14}</div>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== promo.description || $[34] !== promo.title) {
    t16 = promo.description && promo.description !== promo.title && <p className="text-[14px] leading-5 text-[#2E2E2E]">{promo.description}</p>;
    $[33] = promo.description;
    $[34] = promo.title;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  let t17;
  if ($[36] !== flashActive || $[37] !== onExpire || $[38] !== promo.endsAt || $[39] !== promo.startsAt || $[40] !== promo.state) {
    t17 = <div className="flex flex-wrap items-center gap-2 text-[13px] leading-[18px] text-[#5C5C5C]">{flashActive && promo.endsAt ? <PromoCountdown endsAt={promo.endsAt} onExpire={onExpire} /> : promo.state === "active" ? <span className="text-emerald-700">Идёт{promo.endsAt ? ` до ${dayTime(promo.endsAt)}` : ""}</span> : <span>{promo.startsAt ? `Начнётся ${dayTime(promo.startsAt)}` : ""}{promo.endsAt ? `, до ${day(promo.endsAt)}` : ""}</span>}</div>;
    $[36] = flashActive;
    $[37] = onExpire;
    $[38] = promo.endsAt;
    $[39] = promo.startsAt;
    $[40] = promo.state;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  let t18;
  if ($[42] !== promo.code) {
    t18 = promo.code && <p className="flex items-center gap-2 text-[14px] leading-5 text-[#1F1F1F]"><Ticket className="size-4 text-osmix-tangelo" aria-hidden={true} />Код <span className="rounded-[6px] bg-osmix-gray-100 px-2 py-0.5 font-semibold tracking-wide">{promo.code}</span></p>;
    $[42] = promo.code;
    $[43] = t18;
  } else {
    t18 = $[43];
  }
  let t19;
  if ($[44] !== fmt || $[45] !== promo.products) {
    t19 = promo.products.length > 0 && <div className="grid grid-cols-4 gap-2">{promo.products.map(p => <Link key={p.id} to={`/product/${p.id}`} className="group flex min-w-0 flex-col gap-1" title={p.name}><div className="aspect-[3/4] overflow-hidden rounded-[4px] bg-osmix-gray-100">{p.photo ? <img src={p.photo} alt={p.name} loading="lazy" className="size-full object-cover transition-transform group-hover:scale-105" /> : null}</div><span className="truncate text-[12px] font-semibold leading-4 text-[#1F1F1F]">{fmt(p.promoPrice ?? p.price)}</span>{p.promoPrice != null && p.promoPrice < p.price && <span className="truncate text-[11px] leading-[14px] text-osmix-icon line-through">{fmt(p.price)}</span>}</Link>)}</div>;
    $[44] = fmt;
    $[45] = promo.products;
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== promo.productCount || $[48] !== promo.source || $[49] !== shop) {
    t20 = shop?.slug && promo.source === "shop" && <Link to={`/shop/${shop.slug}`} className="inline-flex h-9 items-center rounded-[8px] bg-[#1F1F1F] px-3.5 text-[14px] font-semibold leading-5 text-white hover:bg-black">{promo.productCount > 0 ? `Смотреть товары (${promo.productCount})` : "\u0412 \u043C\u0430\u0433\u0430\u0437\u0438\u043D"}</Link>;
    $[47] = promo.productCount;
    $[48] = promo.source;
    $[49] = shop;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== promo.id || $[52] !== promo.state || $[53] !== promo.subscribed || $[54] !== subscribe || $[55] !== toggleSubscription) {
    t21 = promo.state === "scheduled" && <button type="button" onClick={toggleSubscription} disabled={subscribe.isPending} aria-pressed={promo.subscribed} data-testid={`promo-subscribe-${promo.id}`} className={cn("inline-flex h-9 items-center gap-1.5 rounded-[8px] px-3.5 text-[14px] font-semibold leading-5 transition-colors disabled:opacity-60", promo.subscribed ? "bg-osmix-gray-100 text-[#1F1F1F] hover:bg-osmix-gray-200" : "bg-osmix-tangelo text-white hover:bg-osmix-tangelo/90")}>{promo.subscribed ? <BellOff className="size-4" aria-hidden={true} /> : <Bell className="size-4" aria-hidden={true} />}{promo.subscribed ? "\u041D\u0435 \u043D\u0430\u043F\u043E\u043C\u0438\u043D\u0430\u0442\u044C" : "\u041D\u0430\u043F\u043E\u043C\u043D\u0438\u0442\u044C \u043E \u043D\u0430\u0447\u0430\u043B\u0435"}</button>;
    $[51] = promo.id;
    $[52] = promo.state;
    $[53] = promo.subscribed;
    $[54] = subscribe;
    $[55] = toggleSubscription;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== t20 || $[58] !== t21) {
    t22 = <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">{t20}{t21}</div>;
    $[57] = t20;
    $[58] = t21;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== t15 || $[61] !== t16 || $[62] !== t17 || $[63] !== t18 || $[64] !== t19 || $[65] !== t22 || $[66] !== t5) {
    t23 = <article className="flex flex-col gap-3 rounded-[12px] border border-osmix-gray-200 bg-white p-4" data-testid={t5}>{t15}{t16}{t17}{t18}{t19}{t22}</article>;
    $[60] = t15;
    $[61] = t16;
    $[62] = t17;
    $[63] = t18;
    $[64] = t19;
    $[65] = t22;
    $[66] = t5;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  return t23;
}
function _temp5(s_0, index) {
  return <span key={s_0.id}>{index > 0 && " \xB7 "}{s_0.slug ? <Link to={`/shop/${s_0.slug}`} className="hover:text-osmix-tangelo">{s_0.name}</Link> : s_0.name}</span>;
}
function _temp4(e) {
  return toast.error(e instanceof Error ? e.message : "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C");
}
function _temp3(s) {
  return s.token;
}